import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import { Popable } from "react-native-popable";
import { useTranslation } from "react-i18next";
import {
  extractPlannerStepData,
  getProgressPercentage,
  getUserMessagesAfterStep,
  groupStepsIntoWaves,
} from "../../Utils/plannerStepUtils";
import StepDetailPanel from "./StepDetailPanel";
import StepItem from "./StepItem";
import UserMessageItem from "./UserMessageItem";

/**
 * Wave header component for parallel execution groups
 */
const WaveHeader = ({ waveIndex, isParallel, stepsCount, currentTheme }) => {
  const { t } = useTranslation();
  if (!isParallel) return null;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 6,
        paddingHorizontal: 8,
        marginBottom: 4,
        marginTop: waveIndex > 0 ? 8 : 0,
        backgroundColor: currentTheme?.theme?.primary,
        borderRadius: 4,
      }}
    >
      <AppIcon
        icon={AppAssets?.icons?.parallel || AppAssets?.icons?.copy}
        size={12}
        color="#FFFFFF"
        wrapperStyle={{ marginRight: 6 }}
      />
      <AppText
        style={{
          fontSize: currentTheme?.theme?.extra_small_font_size,
          color: "#FFFFFF",
          fontWeight: "600",
        }}
        preventTranslation
      >
        {t("planner.steps.runningConcurrently", { count: stepsCount })}
      </AppText>
    </View>
  );
};

const PlannerStepsViewer = ({
  messages = [],
  activeStep,
  activeSteps = new Set(),
  totalSteps,
  planningExecution,
  primaryAgent,
  workspaceid,
  S3downloadUrlRequest,
  getAgent,
  height,
  wrapperStyle,
  stepsStatusFromArtifacts = [],
  onToggleExpand,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onToggleExpand?.(newExpandedState);
  };

  // Extract and structure planner data from messages. Organisation is
  // dictated by backend metadata (step_id / from_planner / steps_artifacts),
  // surfaced here for any narration-vs-output UI needs via plannerAgentId.
  const plannerData = useMemo(() => {
    return extractPlannerStepData(
      messages,
      planningExecution,
      stepsStatusFromArtifacts,
      primaryAgent?.id,
    );
  }, [
    JSON.stringify(messages),
    planningExecution,
    JSON.stringify(stepsStatusFromArtifacts),
    primaryAgent?.id,
  ]);

  // Override step statuses with real-time SSE activeSteps for parallel visualization
  // This ensures steps show as in_progress when SSE reports them as active
  const enhancedSteps = useMemo(() => {
    if (!plannerData.steps || plannerData.steps.length === 0) return [];

    return plannerData.steps.map((step) => {
      const stepId = step.stepId || step.stepNumber;
      const isActiveFromSSE =
        activeSteps?.has?.(stepId) || activeSteps?.has?.(String(stepId));

      // If SSE says this step is active, override status to in_progress
      if (
        isActiveFromSSE &&
        step.status !== "completed" &&
        step.status !== "failed"
      ) {
        return {
          ...step,
          status: "in_progress",
          isActive: true,
          isPending: false,
        };
      }
      return step;
    });
  }, [plannerData.steps, activeSteps]);

  // Create enhanced plannerData with real-time step statuses
  // IMPORTANT: Recompute waves from enhancedSteps to ensure parallel headers
  // display correctly during execution (not just from plannerData.waves)
  const enhancedPlannerData = useMemo(() => {
    const waves = groupStepsIntoWaves(enhancedSteps);
    return {
      ...plannerData,
      steps: enhancedSteps,
      waves,
      summary: {
        ...plannerData.summary,
        hasParallelWaves: waves.some((w) => w.isParallel),
        parallelStepsCount: waves.reduce(
          (count, wave) => count + (wave.isParallel ? wave.steps.length : 0),
          0,
        ),
      },
    };
  }, [plannerData, enhancedSteps]);

  const progress = useMemo(() => {
    return getProgressPercentage(enhancedPlannerData);
  }, [enhancedPlannerData]);

  // Get selected step
  const selectedStep = useMemo(() => {
    if (selectedStepIndex === null) return null;
    return enhancedPlannerData.steps[selectedStepIndex] || null;
  }, [selectedStepIndex, enhancedPlannerData.steps]);

  const handleStepClick = (stepIndex) => {
    setSelectedStepIndex(selectedStepIndex === stepIndex ? null : stepIndex);
  };

  const handleCloseDetail = () => {
    setSelectedStepIndex(null);
  };

  const hasSteps = enhancedPlannerData.steps.length > 0;

  return (
    <View
      style={[
        {
          flex: isExpanded ? 1 : 0,
          width: isExpanded ? 300 : 40,
          marginLeft: 16,
          marginRight: -4,
          marginBottom: 10,
          padding: isExpanded ? 10 : 0,
          paddingTop: isExpanded ? 15 : 15,
          borderWidth: 1,
          borderRadius: 10,
          borderColor: isExpanded
            ? currentTheme?.theme?.border_color
            : currentTheme?.theme?.transparent,
          maxHeight: height - 100,
          backgroundColor: isExpanded
            ? currentTheme?.theme?.inputBackgroundColor
            : currentTheme?.theme?.transparent,
        },
        wrapperStyle,
      ]}
    >
      {/* Header */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: isExpanded ? "space-between" : "center",
          alignItems: "center",
          marginBottom: isExpanded ? 10 : 0,
          paddingBottom: isExpanded ? 8 : 0,
          borderBottomWidth: isExpanded ? 1 : 0,
          borderBottomColor: currentTheme?.theme?.border_color,
        }}
      >
        {/* Title and step count - only when expanded */}
        {isExpanded && (
          <View style={{ flex: 1 }}>
            <AppText
              fontWeight="bold"
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.font_color,
              }}
            >
              {t("planner.activities.title")}
            </AppText>
            {hasSteps && (
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  color: currentTheme?.theme?.darkGrey,
                  marginTop: 2,
                }}
              >
                {t("planner.activities.stepCount", {
                  completed: enhancedPlannerData.summary.completedSteps,
                  total: enhancedPlannerData.summary.totalSteps,
                })}
              </AppText>
            )}
          </View>
        )}

        {/* Progress indicator - only show when there are steps */}
        {isExpanded && hasSteps && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {/* Status indicator - show Failed or Completed based on planningExecution */}
            {planningExecution === "plan_execution_failed" && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: `${currentTheme?.theme?.red}`,
                  paddingHorizontal: 8,
                  paddingVertical: 3,
                  borderRadius: 4,
                  marginRight: 8,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.warning}
                  size={10}
                  color={currentTheme?.theme?.white}
                  wrapperStyle={{ marginRight: 4 }}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    color: currentTheme?.theme?.white,
                    fontWeight: "600",
                  }}
                >
                  {t("planner.activities.failed")}
                </AppText>
              </View>
            )}
            {planningExecution === "plan_execution_completed" && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: `${currentTheme?.theme?.green}`,
                  paddingHorizontal: 8,
                  paddingVertical: 3,
                  borderRadius: 4,
                  marginRight: 8,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.check}
                  size={10}
                  color={currentTheme?.theme?.white}
                  wrapperStyle={{ marginRight: 4 }}
                />
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    color: currentTheme?.theme?.white,
                    fontWeight: "600",
                  }}
                >
                  {t("planner.activities.completed")}
                </AppText>
              </View>
            )}
            <View
              style={{
                width: 50,
                height: 4,
                backgroundColor: currentTheme?.theme?.grey,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <View
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor:
                    planningExecution === "plan_execution_failed"
                      ? currentTheme?.theme?.red
                      : currentTheme?.theme?.primary,
                  borderRadius: 2,
                }}
              />
            </View>
            <AppText
              style={{
                fontSize: currentTheme?.theme?.extra_small_font_size,
                color: currentTheme?.theme?.darkGrey,
                marginLeft: 6,
              }}
              preventTranslation
            >
              {progress}%
            </AppText>
          </View>
        )}

        {/* Toggle Button with Popable - on the right side */}
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 200000000,
              maxWidth: 300,
            },
          ]}
          content={
            isExpanded ? t("planner.activities.collapseTooltip") : t("planner.activities.expandTooltip")
          }
          action="hover"
          position="left"
        >
          <AppIcon
            wrapperStyle={{
              padding: 6,
              borderRadius: 6,
              backgroundColor: currentTheme?.theme?.primary_soft,
              marginLeft: isExpanded ? 10 : 0,
            }}
            icon={
              isExpanded
                ? AppAssets?.icons?.arrow_right
                : AppAssets?.icons?.arrow_left
            }
            size={14}
            color={currentTheme?.theme?.primary}
            isClickable
            onPress={toggleExpanded}
          />
        </Popable>
      </View>

      {/* Content - only show when expanded */}
      {isExpanded && (
        <>
          {/* Approval Required Indicator */}
          {(planningExecution === "planning_requires_approval" ||
            planningExecution === "step_requires_approval") && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: `${currentTheme?.theme?.orange}15`,
                borderWidth: 1,
                borderColor: currentTheme?.theme?.orange,
                borderRadius: 8,
                padding: 10,
                marginBottom: 10,
              }}
            >
              <AppIcon
                icon={AppAssets?.icons?.warning || AppAssets?.icons?.info}
                size={16}
                color={currentTheme?.theme?.orange}
                wrapperStyle={{ marginRight: 8 }}
              />
              <View style={{ flex: 1 }}>
                <AppText
                  fontWeight="600"
                  style={{
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.orange,
                  }}
                >
                  {planningExecution === "planning_requires_approval"
                    ? t("planner.activities.planApprovalRequired")
                    : t("planner.activities.stepApprovalRequired")}
                </AppText>
                <AppText
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    color: currentTheme?.theme?.darkGrey,
                    marginTop: 2,
                  }}
                >
                  {planningExecution === "planning_requires_approval"
                    ? t("planner.activities.reviewPlanToContinue")
                    : t("planner.activities.reviewStepToProceed")}
                </AppText>
              </View>
            </View>
          )}

          {/* Placeholder when no steps */}
          {!hasSteps && (
            <AppPlaceholderView
              wrapperStyle={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
              size={24}
              icon={AppAssets?.icons?.planner}
              mainCaption={t("planner.activities.placeholderTitle")}
              subCaption={t("planner.activities.placeholderSubtitle")}
            />
          )}

          {/* Steps Timeline - Grouped by Parallel Waves */}
          {hasSteps && (
            <ScrollView
              style={{
                flex: 1,
                overflowY: Platform.OS === "web" ? "auto" : "scroll",
              }}
              showsVerticalScrollIndicator={true}
            >
              {/* Render steps grouped by waves for parallel visualization */}
              {enhancedPlannerData.waves && enhancedPlannerData.waves.length > 0
                ? enhancedPlannerData.waves.map((wave, waveIndex) => {
                    const waveSteps = wave.steps
                      .map((stepIndex) => enhancedPlannerData.steps[stepIndex])
                      .filter(Boolean);

                    return (
                      <React.Fragment key={`wave-${waveIndex}`}>
                        {/* Wave header for parallel groups */}
                        <WaveHeader
                          waveIndex={waveIndex}
                          isParallel={wave.isParallel}
                          stepsCount={waveSteps.length}
                          currentTheme={currentTheme}
                        />

                        {/* Steps in this wave */}
                        <View
                          style={{
                            marginLeft: wave.isParallel ? 8 : 0,
                            borderLeftWidth: wave.isParallel ? 1 : 0,
                            borderLeftColor: `${currentTheme?.theme?.primary}30`,
                            paddingLeft: wave.isParallel ? 8 : 0,
                          }}
                        >
                          {waveSteps.map((step, indexInWave) => {
                            const globalIndex = step.stepIndex;
                            const isLastInWave =
                              indexInWave === waveSteps.length - 1;
                            const isLastWave =
                              waveIndex ===
                              enhancedPlannerData.waves.length - 1;
                            const isLast = isLastInWave && isLastWave;

                            // Get user messages that came after this step
                            const userMessagesAfter = getUserMessagesAfterStep(
                              enhancedPlannerData,
                              step.stepIndex,
                            );

                            // Check if step is actively streaming via SSE
                            const stepId = step.stepId || step.stepNumber;
                            const isActivelyStreaming =
                              activeSteps?.has?.(stepId) ||
                              activeSteps?.has?.(String(stepId));

                            return (
                              <React.Fragment key={step.id}>
                                <StepItem
                                  step={step}
                                  isSelected={selectedStepIndex === globalIndex}
                                  onClick={() => handleStepClick(globalIndex)}
                                  currentTheme={currentTheme}
                                  isLast={isLast}
                                  getAgent={getAgent}
                                  isParallel={wave.isParallel}
                                  parallelCount={
                                    wave.isParallel ? waveSteps.length : 0
                                  }
                                  allSteps={enhancedPlannerData.steps}
                                  isActivelyStreaming={isActivelyStreaming}
                                />

                                {/* User messages after this step */}
                                {userMessagesAfter.map((userMsg) => (
                                  <UserMessageItem
                                    key={userMsg.messageId}
                                    message={userMsg}
                                    currentTheme={currentTheme}
                                  />
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </View>
                      </React.Fragment>
                    );
                  })
                : // Fallback to sequential rendering if no waves computed
                  enhancedPlannerData.steps.map((step, index) => {
                    const userMessagesAfter = getUserMessagesAfterStep(
                      enhancedPlannerData,
                      step.stepIndex,
                    );

                    // Check if step is actively streaming via SSE
                    const stepId = step.stepId || step.stepNumber;
                    const isActivelyStreaming =
                      activeSteps?.has?.(stepId) ||
                      activeSteps?.has?.(String(stepId));

                    return (
                      <React.Fragment key={step.id}>
                        <StepItem
                          step={step}
                          isSelected={selectedStepIndex === index}
                          onClick={() => handleStepClick(index)}
                          currentTheme={currentTheme}
                          isLast={
                            index === enhancedPlannerData.steps.length - 1
                          }
                          getAgent={getAgent}
                          isParallel={false}
                          parallelCount={0}
                          allSteps={enhancedPlannerData.steps}
                          isActivelyStreaming={isActivelyStreaming}
                        />

                        {/* User messages after this step */}
                        {userMessagesAfter.map((userMsg) => (
                          <UserMessageItem
                            key={userMsg.messageId}
                            message={userMsg}
                            currentTheme={currentTheme}
                          />
                        ))}
                      </React.Fragment>
                    );
                  })}
            </ScrollView>
          )}

          {/* Detail Panel */}
          {selectedStep && (
            <StepDetailPanel
              step={selectedStep}
              onClose={handleCloseDetail}
              currentTheme={currentTheme}
              S3downloadUrlRequest={S3downloadUrlRequest}
              workspaceid={workspaceid}
              getAgent={getAgent}
            />
          )}
        </>
      )}
    </View>
  );
};

export default PlannerStepsViewer;
